<template>
  <div class="ap-scheduler-job-detail">
    <div class="body-container">
      <div class="detail-info">
        <span class="company-name">{{ companyName }}</span><span class="task-name">{{ taskName }}</span>
      </div>
      <div class="table-container" v-rect-loading="loading">
        <NoBorderTable
          class="resume-table"
          :noOperation="true"
          :noHeader="true"
          :tableColumns="tableColumns"
          :listData="dataList"
          :rowClassName="tableRowClassName"
        >
          <template #option="{ row }">
            <span class="view-detail-btn" @click="handelViewDetail(row)">
              {{ $t(`${i18nPrefix}.btn.viewDetail`) }}
            </span>
          </template>
        </NoBorderTable>
        <el-pagination
          class="pagination"
          background
          layout="total, sizes, prev, pager, next"
          :total="pageInfo.total"
          :page-count="pageInfo.page"
          :page-size="pageInfo.pageSize"
          @current-change='handelPageChange'
          @size-change='handelSizeChange'
          >
        </el-pagination>
      </div>
    </div>
    <DialogWrapper
      v-if="detailDialog.show"
      class="detail-dialog"
      destroyOnClose
      :show.sync="detailDialog.show"
      :close-on-click-modal="false"
      :hideFooter="true"
    >
      <template v-if="taskType === 'job'">
        <JobSyncDetailDialog :historyId="detailDialog.historyId"/>
      </template>
      <template v-else-if="['application_status_pull', 'application_status_push'].includes(taskType)">
        <ApplicationStatusSyncDetailDialog :historyId="detailDialog.historyId" :rootCompanyId="rootCompanyId"/>
      </template>
      <template v-else-if="taskType === 'employee_pull'">
        <EmployeeSyncDetailDialog :historyId="detailDialog.historyId"/>
      </template>
      <template v-else>
        <ApplicationSyncDetailDialog :taskName="jobName" :historyId="detailDialog.historyId" :rootCompanyId="rootCompanyId" :isEnabled="isEnabled" @refresh="handleRefresh" />
      </template>
    </DialogWrapper>
  </div>
</template>

<script>

import { isEn } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import NoBorderTable from '@/components/tableList/noBorderTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import JobSyncDetailDialog from './components/jobSyncDetailDialog.vue'
import ApplicationSyncDetailDialog from './components/applicationSyncDetailDialog.vue'
import ApplicationStatusSyncDetailDialog from './components/applicationStatusSyncDetailDialog.vue'
import EmployeeSyncDetailDialog from './components/employeeSyncDetailDialog.vue'

export default {
  components: {
    NoBorderTable,
    DialogWrapper,
    JobSyncDetailDialog,
    ApplicationSyncDetailDialog,
    ApplicationStatusSyncDetailDialog,
    EmployeeSyncDetailDialog
  },
  data () {
    return {
      i18nPrefix: 'admin.apScheduler',
      loading: false,
      navigationArray: [
        {
          i18nName: 'admin.apScheduler.tab.dashboard',
          path: '/v-admin/ap-scheduler/index'
        },
        {
          i18nName: 'admin.apScheduler.tab.detail',
          path: '',
          current: true
        }
      ],
      dataList: [],
      pageInfo: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      companyInfo: {},
      jobName: '',
      detailDialog: {
        show: false,
        historyId: 0
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.startTime`),
          dataKey: 'started_time'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.endTime`),
          dataKey: 'finished_time'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.executeStatus`),
          dataKey: 'status',
          formatter: (data, row) => {
            if (!row.is_deleted && data) {
              return this.$t(`${this.i18nPrefix}.executeStatus.${data}`)
            }
            return ''
          }
        }
      ]
      if (this.taskType === 'job') {
        columns.push(
          {
            label: this.$t(`${this.i18nPrefix}.label.openJobCount`),
            dataKey: 'open_job_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.newJobCount`),
            dataKey: 'new_job_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.closeJobCount`),
            dataKey: 'close_job_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.option`),
            dataKey: 'option',
            slotName: 'option'
          })
      } else if (this.taskType === 'application' || this.taskType === 'application_pull') {
        columns.push(
          {
            label: this.$t(`${this.i18nPrefix}.label.successApplicationCount`),
            dataKey: 'success_application_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.failApplicationCount`),
            dataKey: 'fail_application_count'
          },
          {
            // 9399: LV 公司的申请回传是异步，回传是否执行完成由对方系统通知，所以会记录执行中状态，其他公司目前没有这种情况所以不显示这列
            hidden: this.companyInfo.root_company_id !== 9399,
            label: this.$t(`${this.i18nPrefix}.label.pendingApplicationCount`),
            dataKey: 'pending_application_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.option`),
            dataKey: 'option',
            slotName: 'option'
          })
      } else if (['application_status_pull', 'application_status_push'].includes(this.taskType)) {
        columns.push(
          {
            label: this.$t(`${this.i18nPrefix}.label.successCount`),
            dataKey: 'success_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.failCount`),
            dataKey: 'fail_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.option`),
            dataKey: 'option',
            slotName: 'option'
          })
      } else if (this.taskType === 'employee_pull') {
        columns.push(
          {
            label: this.$t(`${this.i18nPrefix}.label.successCount`),
            dataKey: 'success_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.failCount`),
            dataKey: 'fail_count'
          },
          {
            label: this.$t(`${this.i18nPrefix}.label.option`),
            dataKey: 'option',
            slotName: 'option'
          })
      }
      return columns
    },
    apSchedulerJobId () {
      return this.$route.params.apSchedulerJobId
    },
    companyName () {
      if (this.companyInfo) {
        return isEn() ? this.companyInfo.name : this.companyInfo.cn_name
      }
      return ''
    },
    rootCompanyId () {
      return this.companyInfo.root_company_id
    },
    taskType () {
      return this.$route.query.taskType || 'default'
    },
    taskName () {
      const taskType = this.taskType
      let labelKey = 'otherSync'
      if (taskType === 'job') {
        labelKey = 'jobSync'
      } else if (taskType === 'application') {
        labelKey = 'applicationSync'
      } else if (taskType === 'application_pull') {
        labelKey = 'applicationPullSync'
      } else if (taskType === 'employee_pull') {
        labelKey = 'employeePullSync'
      } else if (taskType === 'application_status_pull') {
        labelKey = 'applicationStatusPullSync'
      } else if (taskType === 'application_status_push') {
        labelKey = 'applicationStatusPushSync'
      }
      return this.$t(`${this.i18nPrefix}.label.${labelKey}`)
    },
    isEnabled () {
      return this.$route.query.is_enabled === '1'
    }
  },
  async created () {
    this.initTaskData()
  },
  methods: {
    ...mapActions('apScheduler', ['getSchedulerJobList', 'getSchedulerJobDetail', 'getJobSyncDetailInfo', 'getApplicationSyncDetailInfo', 'getApplicationStatusSyncDetailInfo']),
    initTaskData () {
      this.handelPageChange(1)
    },
    handleRefresh () {
      this.handelPageChange(this.pageInfo.page)
    },
    async handelPageChange (val) {
      this.loading = true
      try {
        let requestMethod = this.getSchedulerJobDetail
        if (this.taskType === 'job') {
          requestMethod = this.getJobSyncDetailInfo
        } else if (this.taskType === 'employee_pull') {
          requestMethod = this.getJobSyncDetailInfo
        } else if (this.taskType === 'application' || this.taskType === 'application_pull') {
          requestMethod = this.getApplicationSyncDetailInfo
        } else if (['application_status_pull', 'application_status_push'].includes(this.taskType)) {
          requestMethod = this.getApplicationStatusSyncDetailInfo
        }
        this.pageInfo.page = val
        const params = {
          id: this.apSchedulerJobId,
          page_num: val,
          page_size: this.pageInfo.pageSize
        }
        const resp = await requestMethod(params)
        if (resp) {
          this.dataList = resp.data_list
          this.pageInfo.total = resp.total_count
          this.companyInfo = resp.company_info
          this.jobName = resp.task_name
        }
      } finally {
        this.loading = false
      }
    },
    handelSizeChange (val) {
      this.pageInfo.pageSize = val
      this.handelPageChange(this.pageInfo.page)
    },
    handleCancel () {
      const back = this.$route.query.back
      if (back) {
        mixedRouterPush(back)
      } else {
        this.$router.push('/v-admin/ap-scheduler/index')
      }
    },
    tableRowClassName ({ row, rowIndex }) {
      if (rowIndex % 2 === 0) {
        return 'gray-background'
      }
    },
    handelViewDetail (row) {
      const detailDialog = this.detailDialog
      detailDialog.show = true
      detailDialog.historyId = row.id
    }
  }
}
</script>

<style lang="scss" scoped>
.ap-scheduler-job-detail {
  position: relative;

  .body-container {
    margin-top: 20px;
    padding: 10px 20px;
    border-radius: 5px;
    background-color: #FFFFFF;

    .detail-info {

      > span {
        font-size: 16px;
      }

      .company-name {
        margin-right: 10px;
      }
    }

    .table-container {
      margin-top: 20px;
    }

  }

  ::v-deep .el-table {
    border: 0px;

    .el-table__header {
      .el-table__cell {
        font-size: 14px;
        padding: 20px 0;
        color: #000000;
        font-weight: 600;

        &.is-leaf {
          background-color: #FFFFFF;
        }
      }
    }

    .el-table__body {
        .el-table__cell {
          padding: 20px 0;

          .cell {
            padding: 0 5px;
          }
        }

        .gray-background {
          background: #f7f7f7;
        }
    }
  }

  .view-detail-btn {
    cursor: pointer;
    color: #0096cc;
  }

  .pagination {
    text-align: right;
    margin-right: 10px;
    padding: 10px;
  }

  ::v-deep .detail-dialog {
    .el-dialog {
      width: 80%;
    }
  }
}
</style>
